/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { TextInput } from "@narmi/design_system";
import React from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface DollarAmountInputProps<T extends FieldValues> {
  control: Control<T, unknown>;
  name: Path<T>;
  label: string;
}

function DollarAmountInput<T extends FieldValues>(props: DollarAmountInputProps<T>) {
  function isValidDollarAmount(value: string) {
    return /^\d*\.?\d{0,2}$/.test(value);
  }

  function formatDisplayValue(value: string | number): string {
    if (typeof value === "number") {
      value = value.toFixed(2);
    }

    if (!value || value === "$") {
      return value;
    }

    const stringValue = String(value);

    const numericValue = stringValue.replace(/[$,]/g, "");
    if (!numericValue) return "$";

    const parts = numericValue.split(".");
    const wholeNumber = parts[0];
    const decimals = parts[1] || "";

    const formattedWhole = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (decimals) {
      return `$${formattedWhole}.${decimals}`;
    }

    return `$${formattedWhole}${numericValue.includes(".") ? "." : ""}`;
  }

  function parseValueForSubmission(value: string): number | undefined {
    if (!value || value === "$") {
      return undefined;
    }

    const numericValue = value.replace(/[$,]/g, "");
    return numericValue ? parseFloat(numericValue) : undefined;
  }

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={function (renderProps) {
        function handleInput(event: React.ChangeEvent<HTMLInputElement>) {
          const rawValue = event.target.value.replace(/[^\d.]/g, "");

          if (!isValidDollarAmount(rawValue)) {
            return;
          }

          if (!rawValue) {
            renderProps.field.onChange("$");
            return;
          }

          renderProps.field.onChange(rawValue);
        }

        function handleFocus() {
          if (!renderProps.field.value) {
            renderProps.field.onChange("$");
          } else if (typeof renderProps.field.value === "number") {
            const stringValue: string = renderProps.field.value.toString();
            renderProps.field.onChange(stringValue);
          }
        }

        function handleBlur() {
          if (renderProps.field.value === "$") {
            renderProps.field.onChange("");
            renderProps.field.onBlur();
            return;
          }

          const numericValue = parseValueForSubmission(renderProps.field.value);
          renderProps.field.onChange(numericValue);
          renderProps.field.onBlur();
        }

        const displayValue = formatDisplayValue(renderProps.field.value);

        return (
          <TextInput
            label={props.label}
            onChange={handleInput}
            onBlur={handleBlur}
            onFocus={handleFocus}
            value={displayValue}
            error={renderProps.fieldState.error?.message}
            inputMode="decimal"
          />
        );
      }}
    />
  );
}

export default DollarAmountInput;
